import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { debugLogForUser } from '../utils/asyncUtils';

interface Props {
  children: ReactNode;
  loadingComponent?: ReactNode;
  timeout?: number; // Timeout in milliseconds
  onTimeout?: () => void;
}

interface State {
  hasError: boolean;
  isLoading: boolean;
  hasTimedOut: boolean;
  error: Error | null;
}

/**
 * Special error boundary that also handles loading timeouts
 * Useful for wrapping components that might get stuck in loading states
 */
class LoadingErrorBoundary extends Component<Props, State> {
  private timeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    
    this.state = {
      hasError: false,
      isLoading: true,
      hasTimedOut: false,
      error: null
    };
  }

  componentDidMount() {
    // Set up timeout if specified
    if (this.props.timeout) {
      this.timeoutId = setTimeout(() => {
        if (this.state.isLoading) {
          console.warn(`LoadingErrorBoundary timed out after ${this.props.timeout}ms`);
          debugLogForUser('amarildo121@gmail.com', 'LoadingErrorBoundary:timeout', {
            timeout: this.props.timeout
          });
          
          this.setState({ hasTimedOut: true, isLoading: false });
          
          if (this.props.onTimeout) {
            this.props.onTimeout();
          }
        }
      }, this.props.timeout);
    }

    // Simulate loading completion after a short delay
    // In real usage, the child component would call setLoaded()
    setTimeout(() => {
      this.setState({ isLoading: false });
    }, 100);
  }

  componentWillUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isLoading: false
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('LoadingErrorBoundary caught an error:', error);
    debugLogForUser('amarildo121@gmail.com', 'LoadingErrorBoundary:error', {
      error: error.message,
      componentStack: errorInfo.componentStack
    });
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      isLoading: true,
      hasTimedOut: false,
      error: null
    });

    // Reset timeout
    if (this.props.timeout) {
      this.timeoutId = setTimeout(() => {
        if (this.state.isLoading) {
          this.setState({ hasTimedOut: true, isLoading: false });
        }
      }, this.props.timeout);
    }
  };

  render() {
    const { children, loadingComponent } = this.props;
    const { hasError, isLoading, hasTimedOut, error } = this.state;

    // Show error state
    if (hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Component Error
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {error?.message || 'An unexpected error occurred'}
            </Typography>
            <Button variant="contained" onClick={this.handleRetry}>
              Retry
            </Button>
          </Alert>
        </Box>
      );
    }

    // Show timeout state
    if (hasTimedOut) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Loading Timeout
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This component is taking longer than expected to load. This might be due to a network issue.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="contained" onClick={this.handleRetry}>
                Try Again
              </Button>
              <Button variant="outlined" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </Box>
          </Alert>
        </Box>
      );
    }

    // Show loading state
    if (isLoading) {
      return loadingComponent || (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Show normal content
    return children;
  }
}

export default LoadingErrorBoundary;